/* eslint-disable react-hooks/exhaustive-deps */

import { Add, ArrowForward } from "@styled-icons/material-sharp";
import React, { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";

import { Envelope } from "@styled-icons/boxicons-regular/";
import LayoutPage from "layouts/LayoutPage";
import PasswordProtectedContent from "components/PasswordProtectedContent";
import { Paypal } from "@styled-icons/simple-icons/";
import SectionHeading from "components/SectionHeading";
import { Truck } from "@styled-icons/fa-solid/";
import background from "assets/replicas/harry-potter/hp-marauders-map/v7/background_global.jpg";
import { fetchReplicasFAQ } from "utils/functions/google-sheets";
import { paypal } from "config";

const Wrapper = styled.div`
  --step-margin-left: 4%;

  --step-arrow-margin-left: 2.2%;
  --step-arrow-icon-rotate: -5deg;

  --step-size: 80px;
  --step-icon-size: 25px;

  ${(props) => props.theme.breakpoints.S} {
    --step-margin-left: 0;
    --step-arrow-margin-left: 0;
    --step-arrow-icon-rotate: 0deg;
    --step-size: 60px;
    --step-icon-size: 25px;
  }

  margin-top: 50px;
  padding: min(120px, 10vw);
  padding-top: 40px;
  padding-bottom: 80px;

  ${(props) => props.theme.breakpoints.S} {
    padding: 2vw;
  }

  display: flex;
  flex-direction: column;
  align-items: stretch;

  background: ${(props) => props.theme.colors.white}DD;
  backdrop-filter: blur(10px);
  border-radius: 8px;

  * {
    color: ${(props) => props.theme.colors.black} !important;
  }

  p {
    text-align: center;
    max-width: 1180px;
    margin: auto;
    padding: 0;
    color: ${(props) => props.theme.colors.black}AA;
    font-size: 15px;
    font-weight: 400;

    ${(props) => props.theme.breakpoints.S} {
      font-size: 14px;
    }
  }

  b {
    font-weight: 600 !important;
  }

  & .warning {
    color: #971b1b !important;
    font-weight: 600;
  }
`;

const SectionTitle = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  line-height: 1.6em;
  text-align: center;

  margin-top: 80px;
  margin-bottom: 15px;

  &:first-of-type {
    margin-top: 20px;
  }

  &::after {
    content: "";
    display: block;
    width: 40px;
    height: 3px;
    margin: 8px auto 0;
    background: ${(p) => p.theme.colors.black}22;
    border-radius: 2px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const ItemsFlow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: stretch;
  margin-top: 10px;
  & > * {
    align-self: stretch;
  }
  align-content: flex-start;

  ${(props) => props.theme.breakpoints.SM} {
    flex: 1;
    flex-direction: column;
    align-items: stretch;
  }
`;

const Item = styled.div`
  flex: 1 1 0;
  min-height: auto;
  padding: 20px;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.black}22;
  background: ${(props) => props.theme.colors.white}22;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  text-align: center;
  align-self: stretch;
`;

const ItemTitle = styled.div`
  color: ${(p) => p.theme.colors.black} !important;
  font-weight: 700;
  font-size: 15px;
  width: 100%;
  text-align: center;
`;

const ItemInfo = styled.div`
  color: ${(p) => p.theme.colors.black} !important;
  font-weight: 700;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    --size: 32px;
    width: var(--size);
    height: var(--size);
    color: ${(props) => props.theme.colors.black}AA !important;
  }
`;

const ItemDesc = styled.div`
  color: ${(p) => p.theme.colors.black}DD !important;
  font-size: 12px;
  line-height: 1.5;
  width: 100%;
  text-align: center;
  flex: 1 1 auto;

  b {
    font-weight: 600;
  }
`;

const ItemDivider = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;

  svg {
    --size: 18px;
    width: var(--size);
    height: var(--size);
    color: ${(p) => p.theme.colors.black}66 !important;

    ${(props) => props.theme.breakpoints.SM} {
      transform: rotate(90deg);
    }
  }
`;

const ItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ReplicaList = styled.div`
  width: 100%;
  margin-top: 30px;

  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReplicaCategory = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  padding: min(20px, 3vw);
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.black}22;
  background: ${(props) => props.theme.colors.white}22;
`;

const ReplicaVersions = styled.div`
  margin-left: min(10px, 1vw);

  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
`;

const ReplicaName = styled.div`
  margin-bottom: 10px;
  font-weight: 700;
  font-size: 16px;

  ${(props) => props.theme.breakpoints.SM} {
    font-size: 15px;
  }
`;

const ReplicaItem = styled.div<{ isLimited?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${(p) => p.theme.colors.black}22;

  :last-of-type {
    border-bottom: none;
  }

  font-size: 14px;
  ${(props) => props.theme.breakpoints.SM} {
    font-size: 12px;
  }
`;

const ReplicaColLeft = styled.div`
  flex: 4;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ReplicaColRight = styled.div`
  min-width: 80px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0px;
  align-items: flex-end;
  text-align: right;
`;

const ReplicaVersion = styled.a<{ isLimited?: boolean }>`
  font-weight: 700;
  color: ${(p) => p.theme.colors.black}CC !important;
  text-decoration: underline;
  text-underline-offset: 2px;

  transition: all 0.2s ease;

  &:hover {
    color: ${(p) => p.theme.colors.black} !important;
    text-decoration-color: ${(p) => p.theme.colors.black};
    text-decoration-thickness: 2px;
  }

  ${(props) =>
    props.isLimited
      ? css`
          color: #bd5500 !important;
        `
      : ""}
`;

const ReplicaDesc = styled.div<{ isLimited?: boolean }>`
  color: ${(p) => p.theme.colors.black}AA !important  ;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;

  ${(props) =>
    props.isLimited
      ? css`
          color: #94521c !important;
        `
      : ""}
`;

const ReplicaPrice = styled.div`
  font-weight: 700;
  color: ${(p) => p.theme.colors.black} !important;
`;

const ReplicaStock = styled.div<{ available?: boolean }>`
  color: ${(props) => (props.available ? `#1a8507` : `#AAA`)} !important;
  font-size: 12px;
  font-weight: 600;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
  min-height: 40vh;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 4px solid ${(p) => p.theme.colors.black}22;
  border-top-color: ${(p) => p.theme.colors.black};
  animation: ${spin} 1s linear infinite;
  margin-bottom: 8px;
`;

const LoadingText = styled.div`
  color: ${(p) => p.theme.colors.black}AA;
  font-size: 14px;
`;

const ReplicasFAQ = () => {
  const envPassword = String(process.env.REACT_APP_REPLICAS_FAQ_PASSWORD || "")
    .toLowerCase()
    .trim();

  const [replicaGroups, setReplicaGroups] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReplicasFAQ({
      spreadsheetId: "1zS54t-v3s_dzjzFapfQnu2l9Lj2fDVNdl1LxHvpZXAw",
    })
      .then((parsed) => {
        const groups = (parsed && parsed.replicasByCategory) || {};
        setReplicaGroups(groups);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load replicas sheet:", err);
        setLoading(false);
      });
  }, []);

  const pricingItems = [
    {
      price: `${paypal.defaultShipping} ${paypal.currency}`,
      title: "Express International Shipping",
      text: "Worldwide express shipping from the EU. Insured. Tracking number provided.",
    },
    {
      price: `${paypal.fees.fixedFee} ${paypal.currency} + ${paypal.fees.percentageFee}%`,
      title: "Transaction Fees",
      text: "Payments via PayPal only, which incurs a transaction fee based on the amount sent.",
    },
    {
      price: "$$$",
      title: "Price of the Replica",
      text: "Prices for each replica are listed below.",
    },
  ];

  const steps = [
    {
      Icon: Envelope,
      title: "Email",
      text: "<b>In-stock</b> replicas can be shipped right away. For <b>out-of-stock</b> replicas, details will be discussed via email.",
    },
    {
      Icon: Paypal,
      title: "Payment",
      text: "I will send you a <b>PayPal</b> invoice for the total price and wait for the payment to be completed.",
    },
    {
      Icon: Truck,
      title: "International Shipping",
      text: "The replica will be shipped out as soon as possible after payment has been completed.",
    },
  ];

  const renderItems = (
    items: Array<{ Icon?: any; price?: string; title: string; text: string }>,
    DividerIcon: any,
  ) => {
    return items.map((item, idx) => {
      const IconComponent = item.Icon;

      return (
        <React.Fragment key={`item-${idx}`}>
          {idx === 0 ? null : (
            <ItemDivider>
              <DividerIcon />
            </ItemDivider>
          )}

          <Item>
            <ItemInfo>
              {IconComponent ? <IconComponent /> : item.price}
            </ItemInfo>
            <ItemTitle>{item.title}</ItemTitle>
            <ItemDesc dangerouslySetInnerHTML={{ __html: item.text }} />
          </Item>
        </React.Fragment>
      );
    });
  };

  return (
    <LayoutPage background={background}>
      <SectionHeading
        title="Prop Replicas"
        subtitle="Frequently Asked Questions"
      />

      <PasswordProtectedContent
        expectedPassword={envPassword}
        localStorageId="pass_replicas_faq"
      >
        <Wrapper>
          <SectionTitle>About</SectionTitle>
          <Content>
            <p>
              My replicas are not available in my e-shop, because I never set
              out to mass-produce them or run an online store. The first replica
              of each piece is something I make for myself, for the love of the
              process and the immersion it provides, not for selling. That said,
              as a collector myself, I think it would be a shame to keep these
              pieces entirely to myself, so I do sell replicas occasionally on a
              case-by-case basis to those that are interested. Many people also
              ask about providing my digital files; I do not offer them, neither
              for free nor for purchase, for a multitude of reasons, with piracy
              and the ease of misuse being the most obvious.
            </p>
            <br />
            <p>
              Every replica is made entirely from scratch by me, from the design
              and prototyping stages to the finished product. This often
              includes creating custom tools, such as book presses, when
              necessary. Each replica is showcased in detail on my website, with
              the goal of creating a high-end, extremely detailed prop -{" "}
              <b>not a toy</b>. Due to the craftsmanship, materials, and
              significant labor required, these replicas are not cheap, and I
              understand they may not be suited for everyone. Please note that
              the most complex replicas can take a very long time to become
              available, as producing them requires a great deal of focus and
              care, and I also have a job, a family, and other projects and
              hobbies.
            </p>
          </Content>

          <SectionTitle>Pricing</SectionTitle>
          <Content>
            <ItemsWrapper>
              <ItemsFlow>{renderItems(pricingItems, Add)}</ItemsFlow>
            </ItemsWrapper>
          </Content>

          <SectionTitle>Ordering Process</SectionTitle>
          <Content>
            <ItemsFlow>{renderItems(steps, ArrowForward)}</ItemsFlow>
          </Content>

          <SectionTitle>Available Replicas</SectionTitle>
          <Content>
            <p>
              Please note that while I try to keep this list up to date, it may
              not always reflect the latest status and can change at any time
              without notice.
            </p>
            <br />
            <p>
              <b>
                If a replica is not in stock, feel free to contact me via email
                to discuss possible options.
              </b>
            </p>

            <ReplicaList>
              {loading ? (
                <LoadingWrapper>
                  <Spinner />
                  <LoadingText>Loading replicas…</LoadingText>
                </LoadingWrapper>
              ) : replicaGroups ? (
                Object.keys(replicaGroups).map((category, i) => {
                  return (
                    <ReplicaCategory key={category}>
                      <ReplicaName>{category}</ReplicaName>
                      <ReplicaVersions>
                        {replicaGroups[category].map((row: any, i: number) => {
                          return (
                            <ReplicaItem
                              key={i}
                              isLimited={row.limited || null}
                            >
                              <ReplicaColLeft>
                                <ReplicaVersion
                                  href={String(row.link)}
                                  target="_blank"
                                  rel="noreferrer"
                                  isLimited={row.limited || null}
                                >
                                  {row.version || category}
                                </ReplicaVersion>

                                <ReplicaDesc isLimited={row.limited || null}>
                                  {row.description ? row.description : null}
                                </ReplicaDesc>
                              </ReplicaColLeft>

                              <ReplicaColRight>
                                <ReplicaPrice style={{ fontWeight: 700 }}>
                                  {row.price ?? "—"} USD
                                </ReplicaPrice>
                                <ReplicaStock
                                  available={row.instock || row.acceptingorders}
                                >
                                  {row.instock
                                    ? "in stock"
                                    : row.acceptingorders
                                      ? "made to order"
                                      : "waiting list"}
                                </ReplicaStock>
                              </ReplicaColRight>
                            </ReplicaItem>
                          );
                        })}
                      </ReplicaVersions>
                    </ReplicaCategory>
                  );
                })
              ) : null}
            </ReplicaList>
          </Content>

          <SectionTitle>Disclaimers & Cancellation Fees</SectionTitle>
          <Content>
            <ul>
              <li>
                Import Tax: My replicas are shipped from the European Union.
                Shipping within the EU is usually exempt from import tax. For
                shipments outside the EU, please check your local regulations.
                Import tax is applied once the parcel reaches your country, and
                I have no involvement in or control over this process.
              </li>

              <li>
                I'm just a guy making replicas, and to keep things fair and
                comfortable for both sides, I try to minimize the time between
                payment and shipment. For this reason, I operate on a
                trust-based system and do not require any upfront payments or
                deposits. Since I take on the risk of investing significant time
                and materials into your replica, it is expected that you will
                not change your mind once you decide to proceed. Once your
                replica is finished and paid for, any customer-initiated
                cancellation will be subject to the fees stated below, which
                will be deducted from your refund.
              </li>

              <li>
                Once payment has been made, shipping and transaction fees are
                non-refundable in the event of a customer-initiated
                cancellation, regardless of the reason for cancellation, how
                soon after payment it occurs, or whether the replica has already
                been shipped.
              </li>

              <li>
                Cancelling a customized replica (e.g. a Marauder's Map with
                attached extensions) will incur a cancellation fee of 50% of the
                replica's price, due to irreversible changes made according to
                your instructions. This applies only to customer-initiated
                cancellations. Customization begins after payment is received -
                usually immediately - so shipment within 48 hours of payment
                usually still applies.
              </li>

              <li>
                For clarity, customer-initiated cancellation also includes
                refusing or failing to accept the parcel upon delivery - for
                example, due to unpaid import tax. This does not apply to
                situations such as parcels lost in transit, of course.
              </li>

              <li>
                <b>
                  Copying, scanning, reproducing, or sharing my replicas or
                  their digital representations is prohibited, including the
                  distribution or resale of any derived files.
                </b>
              </li>
            </ul>
          </Content>
        </Wrapper>
      </PasswordProtectedContent>
    </LayoutPage>
  );
};

export default ReplicasFAQ;
